import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/*
SPDX-FileCopyrightText: 2023 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { useEffect, useState } from "react";
import { Button, Placeholder, useEffectUpdate, DropdownButtons, PopUpMessage, DownloadModal, EntityMetaToolTip } from "../index";
import { Table as RSTable, Pagination, SelectPicker, Checkbox } from "rsuite";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSliders } from "@fortawesome/free-solid-svg-icons";
import ColumnConfigDrawer from "./ColumnConfigDrawer";
import { exportTableToSpreadsheet, getAllowedFields, getSourceColour } from "./utils";
import Filter from "../filtering/Filter";
import { useStateFallback } from "../hooks/useStateFallback";
function Table(props) {
    const { Column, HeaderCell, Cell } = RSTable;
    let { 
    /* eslint-disable */
    id, data, fieldMeta, height, loading, endpoint, baseUrl, source, page, setPage, pageSize, setPageSize, totalSize, rowCounter, displaySource, filterVisibility, setFilterVisibility, sortColumn, sortType, defaultSort, handleSortColumn, onModalSave, filter, noFilter, noPagination, noSorting, noConfigModal, noDownload, rowSelection, actions, actionsFooter, configButtons,
    /* eslint-enable */
     } = props;
    const [open, setOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadOpen, setDownloadOpen] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("!");
    noFilter = !!noFilter;
    // row selection
    const [selectedRows, setSelectedRows] = useStateFallback(props.selectedRows, props.setSelectedRows, []);
    // @ts-ignore - temp turned off
    const [bulkSelect, setBulkSelect] = useState(false);
    let checked = false;
    let indeterminate = false;
    const noFieldsSelected = fieldMeta.order.active.length === 0;
    if (selectedRows.length === data.length || bulkSelect) {
        checked = true;
    }
    else if (selectedRows.length === 0) {
        checked = false;
    }
    else if (selectedRows.length > 0 && selectedRows.length < data.length) {
        indeterminate = true;
    }
    // @ts-ignore
    const handleCheckAll = (value, checked) => {
        const keys = checked ? data.map((item) => item.id) : [];
        setSelectedRows && setSelectedRows(keys);
    };
    const handleCheck = (value, checked) => {
        const keys = checked
            ? [...selectedRows, value]
            : selectedRows.filter((item) => item !== value);
        setSelectedRows(keys);
    };
    useEffect(() => {
        success &&
            PopUpMessage({
                message: success,
                type: "success",
            });
    }, [success]);
    useEffect(() => {
        error &&
            error !== "!" &&
            PopUpMessage({
                message: error,
                type: "error",
            });
    }, [error]);
    useEffectUpdate(() => {
        checked = false;
        setSelectedRows([]);
    }, [page, pageSize, filter, sortColumn, sortType]);
    const actionDropDownButtons = actions?.map((button) => ({
        ...button,
        action: () => {
            button.action(selectedRows, filter);
        },
        disabled: selectedRows.length === 0,
    }));
    return (_jsxs("div", { style: { height: height }, className: "tol-table", children: [_jsx(DownloadModal, { size: "sm", open: downloadOpen, setOpen: setDownloadOpen, objectType: endpoint, filter: filter, source: source, fields: fieldMeta.order.active, totalSize: totalSize, action: () => exportTableToSpreadsheet(endpoint, fieldMeta.data, filter, sortColumn, sortType, setSuccess, setError, setDownloading, defaultSort, baseUrl) }), _jsx(ColumnConfigDrawer, { open: open, setOpen: setOpen, title: "Add/Remove Table Columns", displaySource: displaySource, customAttributeSelection: getAllowedFields(fieldMeta), onConfigSave: onModalSave, ...props }), _jsxs("div", { className: "tol-table-bar", children: [_jsx("div", { style: { float: "left" }, children: actions && actions.length > 0 && (_jsx(DropdownButtons, { mainButtonIcon: {
                                icon: "paper-plane",
                                type: "primary",
                                position: "left",
                                outline: selectedRows.length === 0,
                            }, dropdownButtons: actionDropDownButtons, footer: actionsFooter, placement: "rightStart" })) }), !noPagination && fieldMeta.order.active.length > 0 && (_jsxs(_Fragment, { children: [rowCounter ? rowCounter : totalSize, _jsx("span", { className: "tol-page-size", children: _jsx(SelectPicker, { value: pageSize, onChange: setPageSize, size: "sm", cleanable: false, searchable: false, data: [
                                        { label: "25", value: 25 },
                                        { label: "50", value: 50 },
                                        { label: "100", value: 100 },
                                        { label: "100", value: 250 },
                                    ] }) }), _jsx(Pagination, { className: "tol-pagination", size: "sm", layout: ["skip"], total: totalSize, activePage: page, onChangePage: setPage, limit: pageSize, onChangeLimit: setPageSize }), _jsx(Pagination, { className: "tol-pagination", prev: true, next: true, first: true, last: true, ellipsis: true, boundaryLinks: true, maxButtons: 3, size: "sm", layout: ["pager"], total: totalSize, activePage: page, onChangePage: setPage, limit: pageSize, onChangeLimit: setPageSize })] })), !noConfigModal && (_jsx(Button, { position: "right", type: "primary", onClick: () => {
                            setOpen(true);
                        }, icon: "sliders", outline: true })), !noFilter && (_jsx(Button, { position: "right", active: filterVisibility, type: "primary", onClick: () => setFilterVisibility(!filterVisibility), disabled: noFieldsSelected, icon: "eye-slash", outline: true })), !noDownload && (_jsx(Button, { position: "right", type: "primary", onClick: () => {
                            setDownloadOpen(!downloadOpen);
                        }, disabled: totalSize <= 0 || noFieldsSelected, loading: downloading, icon: "download", disabledTooltip: totalSize >= 1
                            ? "Must have at least one row to download."
                            : undefined, outline: true })), configButtons] }), noFieldsSelected ? (_jsx(Placeholder, { message: _jsxs(_Fragment, { children: ["Please add a field to get started. Click", _jsx(FontAwesomeIcon, { icon: faSliders, size: "lg", style: { padding: "0 10" } }), "to configure."] }), height: height })) : (_jsx("div", { className: "tol-table-inner", children: _jsxs(RSTable, { bordered: true, data: data, headerHeight: !noFilter && filterVisibility ? 85 : 42, loading: loading, sortColumn: sortColumn, sortType: sortType, onSortColumn: handleSortColumn, rowClassName: (rowData) => {
                        if (rowData) {
                            if (bulkSelect) {
                                return "tol-selected-row disabled";
                            }
                            else if (selectedRows.some((item) => item === rowData.id)) {
                                return "tol-selected-row";
                            }
                        }
                        return "";
                    }, fillHeight: true, wordWrap: true, renderLoading: () => (_jsx(Placeholder, { loader: true, height: height, opacity: 0.8, squareCorners: true })), children: [rowSelection && (_jsxs(Column, { width: 60, children: [_jsx(HeaderCell, { children: _jsx(Checkbox, { className: "tol-row-selection", checked: checked, indeterminate: indeterminate, disabled: bulkSelect || data.length === 0, onChange: handleCheckAll, style: data.length === 0 ? { display: "none" } : {} }) }), _jsx(Cell, { dataKey: "id", children: (rowData) => {
                                        return (_jsx(Checkbox, { className: "tol-row-selection", value: rowData.id, checked: bulkSelect ||
                                                selectedRows.some((item) => item === rowData.id), disabled: bulkSelect, onChange: handleCheck }));
                                    } })] }, "rowSelection")), fieldMeta.order.active.map((key) => {
                            const field = fieldMeta.data[key];
                            const sortable = noSorting ? false : field.sort;
                            const filterable = noFilter ? false : field.filter;
                            console.log(field.source, field.rename);
                            return (_jsxs(Column, { width: field.width, sortable: sortable, fixed: field.fixed, children: [_jsxs(HeaderCell, { children: [(field.description || field.source) && (_jsx("div", { className: "tol-header-info", children: _jsx(EntityMetaToolTip, { baseUrl: baseUrl, field: key, endpoint: endpoint }) })), _jsxs("p", { className: "tol-header-text", children: [field.source && (_jsx("span", { className: "inline-source", style: {
                                                            backgroundColor: getSourceColour(field.source),
                                                        } })), field.rename] }), filterable && (_jsx("span", { className: filterVisibility ? "tol-filter" : "tol-filter-hide", children: _jsx(Filter, { attribute: key, rename: field.rename, type: field.filter, componentId: id, ...props }) }))] }), _jsx(Cell, { dataKey: key })] }, key));
                        })] }) }))] }));
}
export default Table;
